import { faBurst, faDoorClosed, faDoorOpen, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../Icon";
import { useState } from "react";
import { type Combatant, type DisclosureLevel, type ProjectedCombatant, isOpenDoor, disclosureOf } from "@lancer-vtt/domain";
import type { UseEncounter } from "../useEncounter";
import { setDisclosureCommand } from "../disclosure";
import type { EncounterDispatch } from "../StatusEditor";
import { buildDetonateMineIntent } from "../attackIntent";

/**
 * The GM's conceal toggle — flips {@link Combatant.concealed} via the
 * GM-only manual-tier `set_concealed` command. Concealment is a *projection* fact, not a rules
 * status: a concealed unit is broadcast-time absent from every player client (board, rails,
 * tracker, wire — enforced server-side at `EncounterRoom`'s per-socket projection), while the GM
 * sees everything. Orthogonal to RAW `Hidden` (a game-mechanical status with its own reveal
 * rules) — don't conflate them. Rendered only for a GM viewer (the host threads `canConceal`);
 * the server gate (`canIssueEncounter`) stays the wall, this is the affordance mirror.
 */
export function ConcealToggle({
  combatant: c,
  actorId,
  dispatch,
}: {
  // `ProjectedCombatant` (not the bare `Combatant`) — GM-only chrome, so `c` is never actually
  // masked/HP-hidden at runtime, but this control only reads `concealed`/`name`/`id`, which every
  // disclosure tier preserves — no discrimination needed, just the wider type.
  combatant: ProjectedCombatant;
  actorId: string;
  dispatch: EncounterDispatch;
}) {
  const on = c.concealed === true;
  return (
    <button
      type="button"
      className={`cw-conceal${on ? " is-on" : ""}`}
      aria-pressed={on}
      disabled={!actorId}
      title={
        on
          ? `${c.name} is CONCEALED — hidden from every player client. Click to reveal (players see it pop in).`
          : `Conceal ${c.name} from every player client (board, rails, tracker, wire). You still see it, ghosted.`
      }
      onClick={() => actorId && dispatch({ type: "set_concealed", actorId, combatantId: c.id, concealed: !on })}
    >
      <Icon icon={faEyeSlash} className="icon-lead" />
      {on ? "CONCEALED" : "CONCEAL"}
    </button>
  );
}

/**
 * The GM's **3-state DISCLOSURE segmented control** — CLASS → LOADOUT → FULL, mirroring the `.cw-conceal` toggle's placement idiom right
 * beside it. GM-only, available in both EDIT and PLAY (unlike `ConcealToggle`, which happens to be
 * rendered in both today too — this control follows the same host set). Issues `set_disclosure`
 * (GM-only at the server gate, undoable). Displays `disclosureOf(c)`, which applies the RAW default:
 * a never-explicitly-set hostile reads as `"class"` here, even though the
 * reducer/projection still distinguish "never set" internally for the undo-inverse machinery. GM
 * sees the real stat block regardless of the level shown here (own view is always identity) —
 * this control drives what OTHER viewers get, not what the GM sees.
 */
export function DisclosureControl({
  combatant: c,
  actorId,
  dispatch,
}: {
  combatant: ProjectedCombatant;
  actorId: string;
  dispatch: EncounterDispatch;
}) {
  const level = disclosureOf(c);
  const levels: DisclosureLevel[] = ["class", "loadout", "full"];
  return (
    <div className="cw-subtoggle cw-disclosure" role="group" aria-label="disclosure level">
      {levels.map((l) => (
        <button
          key={l}
          type="button"
          className={`cw-chip${level === l ? " cw-chip-on" : ""}`}
          aria-pressed={level === l}
          disabled={!actorId}
          title={`Disclose ${c.name} at the "${l}" tier to players`}
          onClick={() => actorId && l !== level && dispatch(setDisclosureCommand({ actorId, combatantId: c.id, disclosure: l }))}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/**
 * The GM-only **OPEN/SHUT door toggle** — rendered beside the
 * {@link StatusEditor} surface for any combatant authored as a door (`base.objectProfile?.door`,
 * `null` render for everything else). Issues the manual-tier `set_door_state` (GM-only at the
 * server gate); the reducer resolves the whole `objectGroupMembers` group and
 * toggles every LIVE section in ONE command, so this button only ever names the CLICKED section —
 * the group-wide fan-out is the reducer's job, not this component's. A reject (e.g. shutting onto
 * an occupied doorway, point 6) surfaces **verbatim**, inline: this control is
 * self-contained (its own local error state) rather than threaded through a host `onError`, since
 * {@link EditInspectorBody} — one of its two hosts — carries no error-surfacing slot today (unlike
 * {@link BaseEditPanel}'s host-threaded `onError`, the pattern doesn't fit both hosts uniformly).
 * Read via {@link isOpenDoor} (never a bare `hasStatus(c.statuses, "Open")`) — the one predicate
 * that carries the door-marker containment; every open/shut-aware seam routes through
 * it, and this button is no exception.
 */
export function DoorToggle({
  combatant: c,
  actorId,
  dispatch,
}: {
  combatant: Combatant;
  actorId: string | null;
  dispatch: EncounterDispatch;
}) {
  const [rejected, setRejected] = useState<string | null>(null);
  if (c.base.objectProfile?.door !== true) return null;
  const open = isOpenDoor(c);
  const toggle = () => {
    if (!actorId) return;
    const res = dispatch({ type: "set_door_state", actorId, unit: c.id, open: !open });
    setRejected(res.ok ? null : res.error);
  };
  return (
    <div className="cw-door">
      <button
        type="button"
        className={`cw-door-toggle${open ? " is-open" : ""}`}
        disabled={!actorId}
        aria-pressed={open}
        title={open ? `Shut ${c.name}` : `Open ${c.name}`}
        onClick={toggle}
      >
        <Icon icon={open ? faDoorClosed : faDoorOpen} className="icon-lead" />
        {open ? "SHUT DOOR" : "OPEN DOOR"}
      </button>
      {rejected && <div className="cw-error">Rejected: {rejected}</div>}
    </div>
  );
}

/**
 * The **DETONATE** control — rendered on a **mine's own inspector** (a mine is a `kind: "deployable"` combatant,
 * so it falls through {@link InspectorBody}'s generic body below, alongside {@link DoorToggle} and
 * {@link BaseEditPanel} — mines get no dedicated control surface the way NPC/mech/pilot do) when
 * the mine carries a {@link "@lancer-vtt/domain".MineProfile} with a real `deployedBy` — a mine
 * minted with none (a bespoke GM-authored fixture) names no combatant to attribute the intent to
 * and can never be remotely detonated (RAW's remote trigger is the deployer's own choice).
 *
 * **Deployer-scoped AT SPAWN, and it costs no NEW legality wall — but `canControl` here is now
 * only an approximation of "am I the deployer," not a guarantee of it.** `spawnMine`'s
 * `controllerSlotId` is a SNAPSHOT of the deployer's slot at deploy time (`interpretDeployMine`,
 * `packages/rules/src/encounter/effects/interpret.ts:1481`, `near.controllerSlotId`) — this
 * renders inside the SAME `canControl` gate the generic body already wraps {@link DoorToggle}/
 * {@link InspectorActionBar}/{@link BaseEditPanel} in, which reads that snapshot. **That snapshot can drift**: the GM-only `set_controller`
 * command reassigns a mine's `controllerSlotId` independently of `mineProfile.deployedBy` (deploy-
 * inheritance is a one-time mint, never a live link — invariant #5), so a mine handed to a NEW
 * controller who isn't its original deployer will still show this button to them. That's a dead
 * button, not a privilege escalation (the same shape as invariant #3's door toggle): the dispatched
 * intent always attributes to the ORIGINAL `deployedBy` combatant, never the clicking viewer, and
 * the real ownership gate (`canResolveIntent`, keyed on whether the CALLER controls that attributed
 * actor) denies a caller who isn't that deployer's own controller — `checkMineDetonateLegality`
 * (`packages/rules/src/encounter/mine.ts:625`) independently re-confirms `deployedBy === actor` on
 * top of that. A GM sees the control on every mine (the affordance mirror's own GM-anything
 * convention, and the rationale behind it: "click your mine, press DETONATE is what a GM
 * mid-encounter reaches for") and always round-trips legally via `canResolveIntent`'s `isGm`
 * bypass. **No client-side legality is added** — the button just fires `request_detonate_mine` and
 * lets the categorized rejection toast carry any refusal (including the new dead-button case above),
 * exactly like every other self-only intent button in this file (Hide/Disengage/Overcharge/…).
 *
 * **Concealment needs no extra gate here either.** A concealed mine a non-deployer/non-GM viewer
 * isn't entitled to see is never even IN that client's `state.combatants` — {@link
 * "@lancer-vtt/domain".visibleTo} omits it at the projection layer entirely (the owner/GM-only
 * omission the whole board already relies on) — so this component can't mount for the wrong viewer
 * in the first place; {@link InspectorBody}'s own null-combatant guard covers that case.
 */
export function MineDetonateControl({ combatant: c, sendIntent }: { combatant: Combatant; sendIntent: UseEncounter["sendIntent"] }) {
  const deployedBy = c.mineProfile?.deployedBy;
  if (deployedBy === undefined) return null;
  return (
    <div className="cw-mine-detonate">
      <button
        type="button"
        className="cw-mine-detonate-btn"
        title={`Remotely detonate ${c.name} as a Quick action — only its deployer can trigger it.`}
        onClick={() => sendIntent(buildDetonateMineIntent(deployedBy, c.id))}
      >
        <Icon icon={faBurst} className="icon-lead" />
        DETONATE
      </button>
    </div>
  );
}
